import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Play, Users, CheckCircle, Calendar, ChevronLeft, ChevronRight, X, AlertCircle, Loader2 } from 'lucide-react';
import NumberInput from '../../components/ui/NumberInput';

interface Schedule {
    id: string;
    jobName: string;
    frequency: string;
    time: string; // HH:mm
    dayOfWeek: number | null;
    dayOfMonth: number | null;
    isEnabled: boolean;
    lastRunAt: string | null;
}

interface Gym {
    id: string;
    name: string;
    displayName: string;
}

interface MemberPreview {
    member: {
        id: string;
        fullName: string;
        displayName: string;
        phoneNumber: string;
    };
    gymName: string;
    gymId: string;
    willSkip?: 'opted_out' | 'already_sent_today' | null;
}

interface RunAudit {
    id: string;
    status: string;
    totalCount: number;
    processedCount: number;
    sentCount?: number;
    failedCount?: number;
    skippedCount?: number;
    errorDetails?: string | null;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

// System maintenance job (status transitions), not a WhatsApp message job — it
// now lives under the Scheduled Jobs tab and is filtered out of this list.
const MEMBERSHIP_STATUS_JOB = 'membership_status_transition';

export default function SchedulesTab({ token }: Props) {
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

    // Manual Run State
    const [manualRunSchedule, setManualRunSchedule] = useState<Schedule | null>(null);
    const [currentStep, setCurrentStep] = useState(1);

    // Step 1: Gym Selection
    const [availableGyms, setAvailableGyms] = useState<Gym[]>([]);
    const [selectedGymIds, setSelectedGymIds] = useState<string[]>([]);
    const [gymTotal, setGymTotal] = useState(0);
    const [gymPage, setGymPage] = useState(1);
    const [gymPageSize, setGymPageSize] = useState(10);
    const [loadingGyms, setLoadingGyms] = useState(false);

    // Step 2: Member Selection
    const [qualifiedMembers, setQualifiedMembers] = useState<MemberPreview[]>([]);
    const [memberTotal, setMemberTotal] = useState(0);
    const [memberPage, setMemberPage] = useState(1);
    const [memberPageSize, setMemberPageSize] = useState(10);
    const [loadingMembers, setLoadingMembers] = useState(false);
    // Members unchecked in the preview — everyone qualified is selected by
    // default, so pages the admin never opens still get their messages.
    const [excludedMemberIds, setExcludedMemberIds] = useState<Set<string>>(new Set());

    // Step 4: Run Progress
    const [activeAudit, setActiveAudit] = useState<RunAudit | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        fetchSchedules();
    }, [token]);

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/schedules`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch schedules');
            const data = await response.json();
            // The membership-status (system maintenance) job moved to the
            // Scheduled Jobs tab — only WhatsApp message jobs belong here.
            setSchedules((data.data as Schedule[]).filter(s => s.jobName !== MEMBERSHIP_STATUS_JOB));
        } catch (error) {
            console.error(error);
            toast.error('Failed to load schedules');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (manualRunSchedule && currentStep === 1) {
            fetchManualRunGyms();
        }
    }, [manualRunSchedule, currentStep, gymPage, gymPageSize]);

    useEffect(() => {
        if (manualRunSchedule && currentStep === 2) {
            fetchQualifiedMembers();
        }
    }, [currentStep, memberPage, memberPageSize]);

    const fetchManualRunGyms = async () => {
        if (!manualRunSchedule) return;
        try {
            setLoadingGyms(true);
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/${manualRunSchedule.jobName}/gyms?page=${gymPage}&pageSize=${gymPageSize}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setAvailableGyms(data.data || []);
            setGymTotal(data.total || 0);
        } catch (error) {
            toast.error('Failed to fetch gyms');
        } finally {
            setLoadingGyms(false);
        }
    };

    const fetchQualifiedMembers = async () => {
        if (!manualRunSchedule) return;
        try {
            setLoadingMembers(true);
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/${manualRunSchedule.jobName}/members?page=${memberPage}&pageSize=${memberPageSize}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ gymIds: selectedGymIds })
            });
            const data = await res.json();
            setQualifiedMembers(data.data || []);
            setMemberTotal(data.total || 0);
        } catch (error) {
            toast.error('Failed to fetch members');
        } finally {
            setLoadingMembers(false);
        }
    };

    const startManualRun = async () => {
        if (!manualRunSchedule) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/${manualRunSchedule.jobName}/run`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ gymIds: selectedGymIds, excludedMemberIds: Array.from(excludedMemberIds) })
            });
            const data = await res.json();
            if (data.success) {
                setActiveAudit({
                    id: data.data.auditId,
                    status: 'RUNNING',
                    totalCount: data.data.totalCount,
                    processedCount: 0
                });
                setCurrentStep(4);
                startPolling(data.data.auditId);
            }
        } catch (error) {
            toast.error('Failed to start run');
        }
    };

    const startPolling = (auditId: string) => {
        setIsPolling(true);
        pollIntervalRef.current = setInterval(async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/saas/schedules/runs/${auditId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setActiveAudit(data.data);

                    if (data.data.status !== 'RUNNING') {
                        stopPolling();
                        if (data.data.status === 'COMPLETED') toast.success('Schedule run completed!');
                        if (data.data.status === 'FAILED') toast.error('Schedule run failed: ' + data.data.errorDetails);
                    }
                }
            } catch (error) {
                console.error('Polling error', error);
            }
        }, 2000);
    };

    const stopPolling = () => {
        setIsPolling(false);
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const cancelRun = async () => {
        if (!activeAudit) return;
        try {
            await fetch(`${API_BASE_URL}/api/saas/schedules/runs/${activeAudit.id}/cancel`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success('Cancellation requested');
        } catch (error) {
            toast.error('Failed to cancel');
        }
    };

    const resetManualRun = () => {
        setManualRunSchedule(null);
        setCurrentStep(1);
        setSelectedGymIds([]);
        setAvailableGyms([]);
        setQualifiedMembers([]);
        setMemberTotal(0);
        setExcludedMemberIds(new Set());
        setActiveAudit(null);
        stopPolling();
    };

    const toggleMemberSelection = (memberId: string) => {
        setExcludedMemberIds(prev => {
            const next = new Set(prev);
            if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
            return next;
        });
    };

    // Header checkbox scope: the members visible on the current page.
    const togglePageSelection = () => {
        const pageIds = qualifiedMembers.map(m => m.member.id);
        const allSelected = pageIds.every(id => !excludedMemberIds.has(id));
        setExcludedMemberIds(prev => {
            const next = new Set(prev);
            pageIds.forEach(id => { if (allSelected) next.add(id); else next.delete(id); });
            return next;
        });
    };

    const selectedMemberCount = Math.max(memberTotal - excludedMemberIds.size, 0);

    const selectAllGyms = () => {
        setSelectedGymIds(availableGyms.map(g => g.id));
        setExcludedMemberIds(new Set()); // gym set changed — member exclusions are stale
    };

    const toggleGym = (id: string) => {
        setSelectedGymIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
        setExcludedMemberIds(new Set());
    };

    const handleToggle = async (id: string, currentStatus: boolean) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/schedules/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isEnabled: !currentStatus })
            });
            if (!response.ok) throw new Error('Failed to update status');

            toast.success('Status updated');
            fetchSchedules();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleEditSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingSchedule) return;

        try {
            // Prepare payload
            const { frequency, time, dayOfWeek, dayOfMonth } = editingSchedule;

            const response = await fetch(`${API_BASE_URL}/api/saas/schedules/${editingSchedule.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ frequency, time, dayOfWeek, dayOfMonth })
            });

            if (!response.ok) throw new Error('Failed to update schedule');

            toast.success('Schedule updated');
            setEditingSchedule(null);
            fetchSchedules();
        } catch (error) {
            toast.error('Failed to update schedule');
        }
    };

    if (loading) return <div className="p-4 text-center">Loading schedules...</div>;

    const progressPercent = activeAudit
        ? Math.round((activeAudit.processedCount / (activeAudit.totalCount || 1)) * 100)
        : 0;

    return (
        <div>
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {schedules.map((schedule) => (
                        <li key={schedule.id} className="px-6 py-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-indigo-600 uppercase tracking-wider">{schedule.jobName.replace(/_/g, ' ')}</h3>
                                <div className="mt-1 text-sm text-gray-500">
                                    <span className="font-medium text-gray-900">{schedule.frequency.toUpperCase()}</span> at <span className="font-medium text-gray-900">{schedule.time}</span>

                                    {schedule.frequency === 'weekly' && schedule.dayOfWeek !== null && (
                                        <span className="ml-1">on {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][schedule.dayOfWeek]}</span>
                                    )}
                                    {schedule.frequency === 'monthly' && schedule.dayOfMonth !== null && (
                                        <span className="ml-1">on Day {schedule.dayOfMonth}</span>
                                    )}
                                </div>
                                {schedule.lastRunAt && (
                                    <p className="text-xs text-gray-400 mt-1">Last run: {new Date(schedule.lastRunAt).toLocaleString()}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setManualRunSchedule(schedule)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                    <Play className="h-3.5 w-3.5" />
                                    Run Now
                                </button>
                                <button className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${schedule.isEnabled ? 'bg-green-600' : 'bg-gray-200'
                                    }`}
                                    onClick={() => handleToggle(schedule.id, schedule.isEnabled)}
                                >
                                    <span className="sr-only">Use setting</span>
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${schedule.isEnabled ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                                <button
                                    onClick={() => setEditingSchedule(schedule)}
                                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                >
                                    Edit
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Manual Run Modal */}
            {manualRunSchedule && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-50 rounded-lg">
                                    <Calendar className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Manual Schedule Run</h3>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider">{manualRunSchedule.jobName.replace(/_/g, ' ')}</p>
                                </div>
                            </div>
                            <button onClick={resetManualRun} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-50 transition-colors">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Step Indicator */}
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-center gap-2">
                            {[1, 2, 3, 4].map((step) => (
                                <div key={step} className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep === step ? 'bg-indigo-600 text-white' :
                                        currentStep > step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}>
                                        {currentStep > step ? <CheckCircle className="h-4 w-4" /> : step}
                                    </div>
                                    {step < 4 && <div className={`w-12 h-0.5 ${currentStep > step ? 'bg-green-500' : 'bg-gray-200'}`} />}
                                </div>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {currentStep === 1 && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-medium text-gray-700">
                                            Select Gyms ({selectedGymIds.length} selected)
                                        </div>
                                        <button onClick={selectAllGyms} className="text-xs text-indigo-600 hover:underline font-medium">Select All on Page</button>
                                    </div>

                                    {loadingGyms ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <Loader2 className="h-8 w-8 text-indigo-500 animate-spin mb-2" />
                                            <p className="text-sm text-gray-500">Loading gyms...</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-2">
                                            {availableGyms.map(gym => (
                                                <label key={gym.id} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${selectedGymIds.includes(gym.id) ? 'bg-indigo-50 border-indigo-200 translate-x-1' : 'bg-white border-gray-100 hover:border-gray-300'
                                                    }`}>
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                                                        checked={selectedGymIds.includes(gym.id)}
                                                        onChange={() => toggleGym(gym.id)}
                                                    />
                                                    <span className="ml-3 font-medium text-gray-900">{gym.displayName || gym.name}</span>
                                                </label>
                                            ))}
                                            {availableGyms.length === 0 && <p className="text-center py-8 text-gray-500">No gyms found for this template</p>}
                                        </div>
                                    )}

                                    {/* Pagination */}
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                                        <div className="flex gap-2">
                                            {[10, 20, 50, 100].map(size => (
                                                <button key={size} onClick={() => setGymPageSize(size)} className={`px-2 py-1 text-xs rounded border ${gymPageSize === size ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200'}`}>{size}</button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-gray-500">Page {gymPage} of {Math.ceil(gymTotal / gymPageSize) || 1}</span>
                                            <div className="flex gap-1">
                                                <button disabled={gymPage === 1} onClick={() => setGymPage(p => p - 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
                                                <button disabled={gymPage >= Math.ceil(gymTotal / gymPageSize)} onClick={() => setGymPage(p => p + 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2 p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
                                        <Users className="h-4 w-4" />
                                        <span>
                                            Previewing qualified members ({memberTotal} total identified,{' '}
                                            <strong>{selectedMemberCount} selected</strong>)
                                        </span>
                                    </div>

                                    {loadingMembers ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <Loader2 className="h-8 w-8 text-indigo-500 animate-spin mb-2" />
                                            <p className="text-sm text-gray-500">Fetching qualified members...</p>
                                        </div>
                                    ) : (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left">
                                                            <input
                                                                type="checkbox"
                                                                checked={qualifiedMembers.length > 0 && qualifiedMembers.every(m => !excludedMemberIds.has(m.member.id))}
                                                                onChange={togglePageSelection}
                                                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                                                                title="Select / unselect all on this page"
                                                            />
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Member</th>
                                                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Gym</th>
                                                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Phone</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {qualifiedMembers.map((m, idx) => {
                                                        const isSelected = !excludedMemberIds.has(m.member.id);
                                                        return (
                                                            <tr
                                                                key={idx}
                                                                onClick={() => toggleMemberSelection(m.member.id)}
                                                                className={`cursor-pointer transition-colors ${isSelected ? 'hover:bg-gray-50' : 'bg-gray-50/70 opacity-60'}`}
                                                            >
                                                                <td className="px-4 py-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleMemberSelection(m.member.id)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                                                                    />
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={isSelected ? '' : 'line-through text-gray-400'}>
                                                                            {m.member.displayName || m.member.fullName}
                                                                        </span>
                                                                        {m.willSkip === 'opted_out' && (
                                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700" title="This member opted out of WhatsApp — the run will skip them even if selected">
                                                                                Opted out
                                                                            </span>
                                                                        )}
                                                                        {m.willSkip === 'already_sent_today' && (
                                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600" title="Already received this notification today — the run will skip them to avoid a duplicate">
                                                                                Sent today
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{m.gymName}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500 font-mono">{m.member.phoneNumber}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {qualifiedMembers.length === 0 && (
                                                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No members qualified for this run</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Member Pagination */}
                                    <div className="flex items-center justify-between pt-4">
                                        <div className="flex gap-2">
                                            {[10, 20, 50, 100].map(size => (
                                                <button key={size} onClick={() => setMemberPageSize(size)} className={`px-2 py-1 text-xs rounded border ${memberPageSize === size ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200'}`}>{size}</button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-gray-500">Page {memberPage} of {Math.ceil(memberTotal / memberPageSize) || 1}</span>
                                            <div className="flex gap-1">
                                                <button disabled={memberPage === 1} onClick={() => setMemberPage(p => p - 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
                                                <button disabled={memberPage >= Math.ceil(memberTotal / memberPageSize)} onClick={() => setMemberPage(p => p + 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 3 && (
                                <div className="space-y-6">
                                    <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 space-y-4">
                                        <h4 className="font-bold text-gray-900 flex items-center gap-2">
                                            <AlertCircle className="h-5 w-5 text-indigo-600" />
                                            Confirm Schedule Execution
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4 pt-2">
                                            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Target Job</p>
                                                <p className="font-semibold text-gray-900">{manualRunSchedule.jobName.replace(/_/g, ' ')}</p>
                                            </div>
                                            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Total Gyms</p>
                                                <p className="font-semibold text-gray-900">{selectedGymIds.length}</p>
                                            </div>
                                            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm col-span-2 text-center">
                                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Members Selected</p>
                                                <p className="text-2xl font-black text-indigo-600">{selectedMemberCount}</p>
                                                {excludedMemberIds.size > 0 && (
                                                    <p className="text-xs text-gray-400 mt-1">{excludedMemberIds.size} of {memberTotal} qualified member{memberTotal !== 1 ? 's' : ''} unselected</p>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 italic leading-relaxed pt-2">
                                            Warning: Clicking run will immediately start sending WhatsApp notifications to the selected members. This process will follow rate limits but is irreversible.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {currentStep === 4 && activeAudit && (
                                <div className="space-y-6 flex flex-col items-center justify-center py-8">
                                    <div className="text-center space-y-2">
                                        <h4 className="text-xl font-bold text-gray-900">
                                            {activeAudit.status === 'RUNNING' ? 'Processing Messages...' :
                                                activeAudit.status === 'COMPLETED' ? 'Run Successful!' :
                                                    activeAudit.status === 'CANCELLED' ? 'Run Cancelled' : 'Run Failed'}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            {activeAudit.processedCount} of {activeAudit.totalCount} messages processed
                                        </p>
                                    </div>

                                    {/* Cool Progress Bar */}
                                    <div className="w-full max-w-md h-4 bg-gray-100 rounded-full overflow-hidden shadow-inner border border-gray-100">
                                        <div
                                            className={`h-full transition-all duration-500 ease-out rounded-full shadow-lg ${activeAudit.status === 'FAILED' ? 'bg-red-500' :
                                                activeAudit.status === 'CANCELLED' ? 'bg-orange-400' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600'
                                                }`}
                                            style={{ width: `${progressPercent}%` }}
                                        />
                                    </div>
                                    <div className={`text-2xl font-black text-gray-900 ${isPolling ? 'animate-pulse' : ''}`}>{progressPercent}%</div>

                                    {activeAudit.status !== 'RUNNING' && (
                                        <div className="flex items-center gap-2">
                                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">{activeAudit.sentCount ?? 0} sent</span>
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${(activeAudit.failedCount ?? 0) > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{activeAudit.failedCount ?? 0} failed</span>
                                            {(activeAudit.skippedCount ?? 0) > 0 && (
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700" title="Skipped: opted out, already sent today, or missing phone number">{activeAudit.skippedCount} skipped</span>
                                            )}
                                        </div>
                                    )}

                                    {activeAudit.status === 'RUNNING' && (
                                        <button
                                            onClick={cancelRun}
                                            className="px-6 py-2 border-2 border-red-500 text-red-500 font-bold rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            Cancel Run
                                        </button>
                                    )}

                                    {(activeAudit.status !== 'RUNNING') && (
                                        <button
                                            onClick={resetManualRun}
                                            className="px-8 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all shadow-lg hover:shadow-xl"
                                        >
                                            Finish & Close
                                        </button>
                                    )}

                                    {activeAudit.errorDetails && (
                                        <div className="p-4 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100 max-w-md">
                                            <p className="font-bold mb-1">Error encountered:</p>
                                            {activeAudit.errorDetails}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {currentStep < 4 && (
                            <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                                <button
                                    onClick={() => currentStep === 1 ? resetManualRun() : setCurrentStep(s => s - 1)}
                                    className="px-4 py-2 flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {currentStep === 1 ? 'Cancel' : 'Back'}
                                </button>

                                <button
                                    disabled={currentStep === 1 && selectedGymIds.length === 0 || (currentStep === 2 && selectedMemberCount === 0)}
                                    onClick={() => currentStep === 3 ? startManualRun() : setCurrentStep(s => s + 1)}
                                    className="px-6 py-2 flex items-center gap-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all active:scale-95"
                                >
                                    {currentStep === 3 ? 'Start Dispatch' : 'Next Step'}
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingSchedule && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Schedule: {editingSchedule.jobName}</h3>
                        <form onSubmit={handleEditSave}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Frequency</label>
                                    <select
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                        value={editingSchedule.frequency}
                                        onChange={(e) => setEditingSchedule({ ...editingSchedule, frequency: e.target.value })}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Time (HH:MM)</label>
                                        <input
                                            type="time"
                                            required
                                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            value={editingSchedule.time}
                                            onChange={(e) => setEditingSchedule({ ...editingSchedule, time: e.target.value })}
                                        />
                                    </div>

                                    {editingSchedule.frequency === 'weekly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Day of Week</label>
                                            <select
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                                value={editingSchedule.dayOfWeek || 0}
                                                onChange={(e) => setEditingSchedule({ ...editingSchedule, dayOfWeek: parseInt(e.target.value) })}
                                            >
                                                <option value={0}>Sunday</option>
                                                <option value={1}>Monday</option>
                                                <option value={2}>Tuesday</option>
                                                <option value={3}>Wednesday</option>
                                                <option value={4}>Thursday</option>
                                                <option value={5}>Friday</option>
                                                <option value={6}>Saturday</option>
                                            </select>
                                        </div>
                                    )}

                                    {editingSchedule.frequency === 'monthly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Day of Month</label>
                                            <NumberInput
                                                integer
                                                min={1}
                                                max={31}
                                                emptyValue={1}
                                                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                value={editingSchedule.dayOfMonth || 1}
                                                onChange={(v) => setEditingSchedule({ ...editingSchedule, dayOfMonth: v ?? 1 })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setEditingSchedule(null)}
                                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
